import { useState } from "react";

import { useInstruments } from "../api/hooks/useInstruments";
import { useRestoreInstrument } from "../api/hooks/useRestoreInstrument";
import { DeleteInstrumentDialog } from "../components/instruments/DeleteInstrumentDialog";
import {
  InstrumentFormDialog,
  type InstrumentResponse,
} from "../components/instruments/InstrumentFormDialog";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

type FormDialogState =
  | { open: false }
  | { open: true; mode: "create" }
  | { open: true; mode: "edit"; instrument: InstrumentResponse };

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toISOString().slice(0, 10);
}

export function InstrumentsPage() {
  const [showDeleted, setShowDeleted] = useState(false);
  const [formDialog, setFormDialog] = useState<FormDialogState>({ open: false });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    instrument: InstrumentResponse | null;
  }>({ open: false, instrument: null });

  const list = useInstruments({ include_deleted: showDeleted });
  const restoreMutation = useRestoreInstrument();

  const items = list.data?.items ?? [];
  const loadErrorMessage =
    list.error instanceof Error
      ? list.error.message
      : "Failed to load instruments.";

  return (
    <section className="p-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">Instruments</h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Manage the instruments you trade. Tickers are uppercased on save.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => {
            setFormDialog({ open: true, mode: "create" });
          }}
        >
          New instrument
        </Button>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>Instruments</CardTitle>
          <label className="flex items-center gap-2 text-sm text-on-surface-variant">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={showDeleted}
              onChange={(event) => {
                setShowDeleted(event.target.checked);
              }}
            />
            Show deleted
          </label>
        </CardHeader>
        <CardContent>
          {list.isLoading ? (
            <p role="status" aria-live="polite" className="text-on-surface-variant">
              Loading instruments…
            </p>
          ) : list.isError ? (
            <p role="alert" className="text-sm font-medium text-danger">
              {loadErrorMessage}
            </p>
          ) : items.length === 0 ? (
            <p className="text-sm text-on-surface-variant">
              No instruments yet. Click &quot;New instrument&quot; to add one.
            </p>
          ) : (
            <Table data-testid="instruments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Symbol</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((instrument) => {
                  const deleted = instrument.is_deleted;
                  return (
                    <TableRow
                      key={instrument.instrument_id}
                      data-deleted={deleted ? "true" : undefined}
                      className={deleted ? "text-on-surface-muted" : undefined}
                    >
                      <TableCell className="font-mono">
                        {instrument.symbol}
                      </TableCell>
                      <TableCell>
                        {instrument.description === null ||
                        instrument.description === ""
                          ? "—"
                          : instrument.description}
                      </TableCell>
                      <TableCell>
                        {formatDate(instrument.created_at)}
                      </TableCell>
                      <TableCell>{deleted ? "Deleted" : "Active"}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          {deleted ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={restoreMutation.isPending}
                              onClick={() => {
                                restoreMutation.mutate({
                                  instrumentId: instrument.instrument_id,
                                });
                              }}
                            >
                              Restore
                            </Button>
                          ) : (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setFormDialog({
                                    open: true,
                                    mode: "edit",
                                    instrument,
                                  });
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="text-danger hover:bg-danger-soft"
                                onClick={() => {
                                  setDeleteDialog({ open: true, instrument });
                                }}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <InstrumentFormDialog
        open={formDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setFormDialog({ open: false });
          }
        }}
        mode={formDialog.open ? formDialog.mode : "create"}
        initialInstrument={
          formDialog.open && formDialog.mode === "edit"
            ? formDialog.instrument
            : null
        }
      />
      <DeleteInstrumentDialog
        open={deleteDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialog({ open: false, instrument: null });
          }
        }}
        instrument={deleteDialog.instrument}
      />
    </section>
  );
}
